'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

const NAV_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Patient Portal', href: '#patient-portal' },
  { label: 'Contact', href: '#contact' },
];

const HERO_SLIDES = [
  {
    bg: "/assets/images/IMG_20260817_233006-1786989695010.jpg",
    alt: 'Chest & Allergy Centre at Adhvay Medicos building — Dr. Neeraj Mumwalia Pulmonologist Jammu',
    badge: 'Our Clinic',
    headline: 'Chest & Allergy Centre',
    sub: 'Adhvay Medicos — Your Trusted Lung Care Destination in Jammu',
  },
  {
    bg: "https://img.rocket.new/generatedImages/rocket_gen_img_15e68271f-1766513916990.png",
    alt: 'Modern pulmonology clinic interior with clean white walls and medical equipment',
    badge: 'We Provide',
    headline: 'Best Pulmonologist in Jammu',
    sub: 'Expert Respiratory & Lung Care Services',
  },
  {
    bg: "https://img.rocket.new/generatedImages/rocket_gen_img_13c19f6b8-1772093391124.png",
    alt: 'Doctor consulting patient in a well-equipped medical clinic',
    badge: 'Specialist Care',
    headline: 'Advanced Bronchoscopy & EBUS',
    sub: 'State-of-the-art Interventional Pulmonology',
  },
  {
    bg: "https://img.rocket.new/generatedImages/rocket_gen_img_1c830d103-1767164233647.png",
    alt: 'Medical professional reviewing lung X-ray scans in a clinic',
    badge: 'Trusted by Thousands',
    headline: 'COPD · Asthma · TB · ILD',
    sub: 'Comprehensive Lung Disease Management',
  },
  {
    bg: "/assets/images/IMG_20260817_152940-1786961104971.jpg",
    alt: 'Chest & Allergy Centre and Adhvay Medicos clinic building exterior in Jammu',
    badge: 'Our Clinic',
    headline: 'Chest & Allergy Centre',
    sub: 'Adhvay Medicos — Your Trusted Lung Care Destination in Jammu',
  },
];

const CAPABILITIES = [
  'Medical Thoracoscopy',
  'Bronchoscopy',
  'Endobronchial Ultrasound (EBUS)',
  'Advanced Pulmonary Function Test with DLCO Machine',
  'Skin Prick Test (For Allergy)',
  'Sleep Study & CPAP Therapy',
];

const TYPES_OF_CARE = [
  {
    title: 'COPD Management',
    desc: 'Comprehensive diagnosis and long-term management of Chronic Obstructive Pulmonary Disease.',
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1447063f5-1767039245568.png",
    alt: 'Doctor examining elderly patient with breathing difficulty for COPD management',
  },
  {
    title: 'Asthma Care',
    desc: 'Personalised asthma action plans, trigger identification, and inhaler technique training.',
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_114ff95f5-1765761804305.png",
    alt: 'Patient using inhaler with doctor guidance for asthma treatment',
  },
  {
    title: 'TB Treatment',
    desc: 'Complete tuberculosis diagnosis, DOTS therapy, and drug-resistant TB management.',
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea4d26fe-1772561503278.png",
    alt: 'Chest X-ray being reviewed by pulmonologist for tuberculosis diagnosis',
  },
  {
    title: 'Interstitial Lung Disease',
    desc: 'Expert evaluation and treatment of ILD including pulmonary fibrosis and sarcoidosis.',
    img: "https://img.rocket.new/generatedImages/rocket_gen_img_1514cf9e3-1785889372518.png",
    alt: 'High resolution CT scan of lungs showing interstitial lung disease patterns',
  },
];

export default function PublicHomePage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [tickerPaused, setTickerPaused] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const router = useRouter();

  // Long-press state for secret doctor access
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressActive = useRef(false);

  const handleLogoPointerDown = () => {
    longPressActive.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressActive.current = true;
      router.push('/doctor-login');
    }, 2000);
  };

  const handleLogoPointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleLogoPointerLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (longPressActive.current) {
      e.preventDefault();
    }
  };

  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    reason: '',
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState('');

  // Returns available doctor consultation time slots based on selected date
  const getAvailableTimeSlots = (dateStr: string): { value: string; label: string }[] => {
    if (!dateStr) return [];
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    if (dayOfWeek === 0) {
      // Sunday: 10:30 AM to 5:00 PM every 30 mins
      const slots: { value: string; label: string }[] = [];
      const times = [
        '10:30 AM', '11:00 AM', '11:30 AM',
        '12:00 PM', '12:30 PM',
        '1:00 PM', '1:30 PM',
        '2:00 PM', '2:30 PM',
        '3:00 PM', '3:30 PM',
        '4:00 PM', '4:30 PM',
        '5:00 PM',
      ];
      times.forEach((t) => slots.push({ value: t, label: t }));
      return slots;
    } else {
      // Mon–Sat: 5:30 PM to 8:00 PM (5 slots)
      return [
        { value: '5:30 PM', label: '5:30 PM' },
        { value: '6:00 PM', label: '6:00 PM' },
        { value: '6:30 PM', label: '6:30 PM' },
        { value: '7:00 PM', label: '7:00 PM' },
        { value: '7:30 PM', label: '7:30 PM' },
      ];
    }
  };

  const availableSlots = getAvailableTimeSlots(bookingForm.date);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToSlide = (idx: number) => {
    setCurrentSlide(idx);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
  };

  const handleBookingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBookingForm((prev) => {
      // Reset time when date changes
      if (name === 'date') {
        return { ...prev, date: value, time: '' };
      }
      return { ...prev, [name]: value };
    });
    setBookingError('');
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBookingLoading(true);
    setBookingError('');

    try {
      const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      if (!url || !key) {
        console.warn('Supabase env vars missing. Logging appointment details instead.');
        console.log('Appointment request:', bookingForm);
        console.log('Email notification to contact@drneerajlungcare.in:', {
          to: 'contact@drneerajlungcare.in',
          subject: `New Appointment Request from ${bookingForm.name}`,
          body: `Name: ${bookingForm.name}\nPhone: ${bookingForm.phone}\nDate: ${bookingForm.date}\nTime: ${bookingForm.time}\nReason: ${bookingForm.reason}`,
        });
        setBookingSuccess(true);
        setBookingForm({ name: '', phone: '', date: '', time: '', reason: '' });
        setBookingLoading(false);
        return;
      }

      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();

      if (!supabase) {
        console.warn('Supabase client unavailable. Logging appointment.');
        console.log('Appointment request:', bookingForm);
        setBookingSuccess(true);
        setBookingForm({ name: '', phone: '', date: '', time: '', reason: '' });
        setBookingLoading(false);
        return;
      }

      const { error } = await supabase.from('appointments').insert({
        name: bookingForm.name.trim(),
        phone: bookingForm.phone.trim(),
        date: bookingForm.date,
        time: bookingForm.time,
        reason: bookingForm.reason.trim(),
        status: 'pending',
      });

      if (error) {
        console.error('Booking error:', error.message);
        setBookingError('Something went wrong. Please try again or call us directly.');
        setBookingLoading(false);
        return;
      }

      // Log email notification (replace with real email API when ready)
      console.log('Email notification to contact@drneerajlungcare.in:', {
        to: 'contact@drneerajlungcare.in',
        subject: `New Appointment Request from ${bookingForm.name}`,
        body: `Name: ${bookingForm.name}\nPhone: ${bookingForm.phone}\nDate: ${bookingForm.date}\nTime: ${bookingForm.time}\nReason: ${bookingForm.reason}`,
      });

      setBookingSuccess(true);
      setBookingForm({ name: '', phone: '', date: '', time: '', reason: '' });
    } catch (err) {
      console.error('Booking submission failed:', err);
      setBookingError('Something went wrong. Please try again or call us directly.');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF8]" style={{ fontFamily: "'Inter', sans-serif" }} id="home">

      {/* ── Announcement Ticker ── */}
      <div
        className="bg-[#8B1A1A] text-white text-xs py-2 overflow-hidden relative"
        onMouseEnter={() => setTickerPaused(true)}
        onMouseLeave={() => setTickerPaused(false)}
      >
        <div
          className="whitespace-nowrap inline-block px-4"
          style={{ animation: tickerPaused ? 'none' : 'ticker 32s linear infinite' }}
        >
          🏥&nbsp; Dr. Neeraj Mumwalia&apos;s Centre for Lung Care and Chest Diseases — Now offering advanced EBUS facility &amp; DLCO Pulmonary Function Testing in Jammu &nbsp;|&nbsp; 📞 Book your appointment today &nbsp;|&nbsp; Specialised care for COPD, Asthma, TB, ILD &amp; Sleep Disorders &nbsp;|&nbsp; 🏥&nbsp; Dr. Neeraj Mumwalia&apos;s Centre for Lung Care and Chest Diseases — Now offering advanced EBUS facility &amp; DLCO Pulmonary Function Testing in Jammu &nbsp;|&nbsp; 📞 Book your appointment today
        </div>
      </div>

      {/* ── Glassmorphism Header ── */}
      <header
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          height: '70px',
          background: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.08)' : '0 1px 0 rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.6)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 select-none"
                style={{
                  background: '#fff',
                  boxShadow: '0 4px 16px rgba(139,26,26,0.18), 0 1px 4px rgba(0,0,0,0.08)',
                  cursor: 'default',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  WebkitTouchCallout: 'none',
                }}
                onPointerDown={handleLogoPointerDown}
                onPointerUp={handleLogoPointerUp}
                onPointerLeave={handleLogoPointerLeave}
                onClick={handleLogoClick}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  src="/assets/images/778565395_1845810629734111_8102026793658882753_n-1786963371580.webp"
                  alt="Centre for Lung Care and Chest Diseases logo"
                  className="w-7 h-7 sm:w-9 sm:h-9 object-contain"
                  draggable={false}
                />
              </div>
              <div>
                {/* Desktop: full name two-line; Mobile: short name */}
                <div className="leading-tight" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: '#111827', letterSpacing: '-0.3px' }}>
                  {/* Mobile: two lines */}
                  <span className="block sm:hidden text-xs leading-tight">
                    Centre for{' '}
                    <span style={{ color: '#8B1A1A' }}>Lung Care</span>
                    <br />
                    <span style={{ fontSize: '11px', fontWeight: 600 }}>and Chest Diseases</span>
                  </span>
                  {/* Desktop: two lines */}
                  <span className="hidden sm:block" style={{ fontSize: '15px' }}>
                    Centre for{' '}
                    <span style={{ color: '#8B1A1A' }}>Lung Care</span>
                    <br />
                    <span style={{ fontSize: '13px', fontWeight: 600 }}>and Chest Diseases</span>
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 500, marginTop: '1px' }}>Dr. Neeraj Mumwalia · MBBS, DNB, MNAMS · Interventional Pulmonologist</div>
              </div>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-semibold transition-colors duration-200 relative group"
                  style={{ color: '#374151' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#A51C30')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#374151')}
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300" style={{ background: '#A51C30' }} />
                </a>
              ))}
              <Link
                href="/patient-login"
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full transition-all duration-200"
                style={{ background: '#A51C30', color: '#fff', boxShadow: '0 4px 14px rgba(165,28,48,0.3)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(165,28,48,0.4)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(165,28,48,0.3)'; }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Patient App
              </Link>
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              <a
                href="#contact"
                className="hidden md:inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200"
                style={{ background: '#A51C30', color: '#fff', boxShadow: '0 4px 14px rgba(165,28,48,0.25)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
              >
                Book Appointment
              </a>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 rounded-xl border transition-colors"
                style={{ borderColor: '#E5E0D8' }}
                aria-label="Toggle menu"
              >
                {menuOpen ? (
                  <svg className="w-5 h-5" style={{ color: '#111827' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" style={{ color: '#111827' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Drawer */}
        {menuOpen && (
          <div className="md:hidden border-t" style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderColor: 'rgba(0,0,0,0.06)' }}>
            <nav className="flex flex-col py-2">
              {NAV_LINKS.map((link, i) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="px-6 py-3 text-sm font-semibold transition-colors duration-150"
                  style={{ color: i === 0 ? '#A51C30' : '#374151' }}
                >
                  {link.label}
                </a>
              ))}
              <div className="px-6 py-3 border-t mt-1" style={{ borderColor: '#F3F0EB' }}>
                <Link
                  href="/patient-login"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-full mb-2 transition-all"
                  style={{ background: '#A51C30', boxShadow: '0 4px 14px rgba(165,28,48,0.3)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Open Patient App
                </Link>
                <a
                  href="#contact"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
                  style={{ border: '1.5px solid #A51C30', color: '#A51C30' }}
                >
                  Book Appointment
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* ── Hero Slider ── */}
      <section className="relative w-full overflow-hidden" style={{ height: 'clamp(480px, 70vh, 680px)' }}>
        {HERO_SLIDES.map((slide, idx) => (
          <div
            key={idx}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: idx === currentSlide ? 1 : 0 }}
          >
            <img
              src={slide.bg}
              alt={slide.alt}
              className="w-full h-full object-cover transition-transform duration-700"
              style={{ transform: idx === currentSlide ? 'scale(1.03)' : 'scale(1)' }}
            />
            {/* Premium dark gradient overlay */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to right, rgba(15,23,42,0.82) 0%, rgba(15,23,42,0.55) 55%, transparent 100%)' }}
            />
          </div>
        ))}

        {/* Slide Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <div className="max-w-lg">
              {/* Badge */}
              <span
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 fade-up"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  color: '#fff',
                  letterSpacing: '0.5px',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#A51C30' }} />
                {HERO_SLIDES[currentSlide].badge}
              </span>

              {/* Headline */}
              <h1
                className="text-white mb-4 fade-up-delay-1"
                style={{
                  fontSize: 'clamp(32px, 5vw, 42px)',
                  fontWeight: 800,
                  letterSpacing: '-1px',
                  lineHeight: 1.15,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {HERO_SLIDES[currentSlide].headline}
              </h1>

              {/* Subtitle */}
              <p
                className="mb-8 fade-up-delay-2"
                style={{ color: 'rgba(255,255,255,0.82)', fontSize: '18px', fontWeight: 400, lineHeight: 1.6 }}
              >
                {HERO_SLIDES[currentSlide].sub}
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 fade-up-delay-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full transition-all duration-200"
                  style={{
                    border: '1.5px solid rgba(255,255,255,0.8)',
                    color: '#fff',
                    backdropFilter: 'blur(8px)',
                    background: 'rgba(255,255,255,0.08)',
                    fontSize: '16px',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.18)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  Contact Us
                </a>
                <Link
                  href="/patient-login"
                  className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full transition-all duration-200"
                  style={{
                    background: '#A51C30',
                    color: '#fff',
                    fontSize: '16px',
                    boxShadow: '0 8px 24px rgba(165,28,48,0.45)',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px) scale(1.02)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(165,28,48,0.55)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0) scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(165,28,48,0.45)'; }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Patient Portal
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {HERO_SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className="rounded-full transition-all duration-300"
              style={{
                width: idx === currentSlide ? '24px' : '8px',
                height: '8px',
                background: idx === currentSlide ? '#fff' : 'rgba(255,255,255,0.45)',
              }}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

        {/* Slide Arrows */}
        <button
          onClick={() => goToSlide((currentSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
          aria-label="Previous slide"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => goToSlide((currentSlide + 1) % HERO_SLIDES.length)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
          aria-label="Next slide"
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </section>

      {/* ── About Us ── */}
      <section id="about" className="py-20 lg:py-24" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Doctor Photo */}
            <div className="relative">
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}
              >
                <img
                  src="/assets/images/784427274_1218730657092019_5948847194196988521_n-1787553363281.webp"
                  alt="Dr. Neeraj Mumwalia MBBS DNB MNAMS Consultant Interventional Pulmonologist professional portrait photo"
                  className="w-full object-contain transition-transform duration-700 hover:scale-105"
                  style={{ display: 'block' }}
                />
              </div>
              {/* Doctor info card — below photo so nothing overlaps */}
              <div className="mt-3 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <p className="font-bold text-lg" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif" }}>Dr. Neeraj Mumwalia</p>
                <p className="text-sm font-semibold" style={{ color: '#A51C30' }}>MBBS, DNB, MNAMS (New Delhi) · DNB Pulmonary Medicine (NITRD)</p>
                <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>Consultant Interventional Pulmonologist · Jammu</p>
              </div>
              {/* Decorative lung icon */}
              <div
                className="absolute -top-5 -left-5 w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: '#FFF5F5', boxShadow: '0 4px 16px rgba(139,26,26,0.12)', border: '1px solid rgba(139,26,26,0.1)' }}
              >
                <svg viewBox="0 0 40 40" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="13" cy="22" rx="7" ry="10" fill="#8B1A1A" opacity="0.8" />
                  <ellipse cx="27" cy="22" rx="7" ry="10" fill="#8B1A1A" opacity="0.8" />
                  <rect x="18" y="10" width="4" height="20" rx="2" fill="#8B1A1A" opacity="0.4" />
                </svg>
              </div>
              {/* We're always here card */}
              {/* Clinic Building Photo */}
              <div className="mt-4 rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                <img
                  src="/assets/images/IMG_20260817_152940-1786961104971.jpg"
                  alt="Chest & Allergy Centre and Adhvay Medicos clinic building exterior in Jammu"
                  className="w-full object-cover transition-transform duration-700 hover:scale-105"
                  style={{ height: '180px', objectPosition: 'center' }}
                />
                <div className="px-4 py-3" style={{ background: '#FFF5F5' }}>
                  <p className="text-xs font-semibold" style={{ color: '#A51C30' }}>Chest &amp; Allergy Centre · Adhvay Medicos</p>
                  <p className="text-xs" style={{ color: '#9CA3AF' }}>Our Clinic — Jammu</p>
                </div>
              </div>

              <div className="mt-4 rounded-2xl p-5 flex items-center gap-4" style={{ background: '#A51C30', boxShadow: '0 8px 32px rgba(165,28,48,0.3)' }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-base">We&apos;re always here to help you</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Compassionate care, every step of the way</p>
                </div>
              </div>
            </div>

            {/* Right: About Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
                <div className="w-3 h-3 rounded-full" style={{ background: '#A51C30' }} />
              </div>
              <h2 className="text-3xl lg:text-4xl font-extrabold mb-6" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.5px' }}>About Us</h2>
              <p className="leading-relaxed mb-4 text-base" style={{ color: '#6B7280' }}>
                Dr. Neeraj Mumwalia&apos;s Centre for Lung Care and Chest Diseases was established with a vision to provide top class pulmonology care to the people of Jammu and the surrounding region. What began as a focused OPD clinic has grown into a comprehensive respiratory care centre equipped with the latest diagnostic and interventional technology.
              </p>
              <p className="leading-relaxed mb-4 text-base" style={{ color: '#6B7280' }}>
                Dr. Neeraj Mumwalia is a Consultant Interventional Pulmonologist holding MBBS, DNB, MNAMS (New Delhi) and DNB Pulmonary Medicine (NITRD, New Delhi). He serves as Assistant Professor (Respiratory Medicine) at GMC Jammu, and has previously worked as Consultant Pulmonologist at Max Hospital New Delhi and Ex-Registrar at Govt Chest Diseases Hospital, GMC Jammu.
              </p>

              {/* Qualifications & Affiliations */}
              <div className="mb-6 rounded-2xl p-5" style={{ background: '#FFF5F5', border: '1px solid rgba(139,26,26,0.08)' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#A51C30' }}>Affiliations</p>
                <ul className="space-y-1.5">
                  {[
                    'Member of American College of Chest Physicians (USA)',
                    'Member of European Respiratory Society (Mem. No. 422142)',
                    'Lifetime Member of Indian Chest Society (L-2620)',
                  ].map((aff) => (
                    <li key={aff} className="flex items-start gap-2">
                      <span className="mt-1 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: '#A51C30' }}>
                        <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-xs font-medium" style={{ color: '#374151' }}>{aff}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Capabilities Checklist */}
              <ul className="space-y-3">
                {CAPABILITIES.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: '#FFF5F5' }}>
                      <svg className="w-3 h-3" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                    <span className="text-sm font-medium" style={{ color: '#374151' }}>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className="mt-8 inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full transition-all duration-200"
                style={{ background: '#A51C30', color: '#fff', boxShadow: '0 4px 20px rgba(165,28,48,0.3)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(165,28,48,0.4)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 20px rgba(165,28,48,0.3)'; }}
              >
                Contact Us
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Types of Care ── */}
      <section id="services" className="py-20 lg:py-24" style={{ background: '#F8F5F0' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.5px' }}>Types of Care</h2>
            <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: '#9CA3AF' }}>
              Specialised respiratory care tailored to your condition — from diagnosis to long-term management.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TYPES_OF_CARE.map((care) => (
              <div
                key={care.title}
                className="group rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-6px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.14)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.08)'; }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={care.img}
                    alt={care.alt}
                    className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-600"
                    style={{ transition: 'transform 0.6s ease' }}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)' }} />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <span className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#A51C30' }}>
                      {care.title}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{care.desc}</p>
                  <a
                    href="#contact"
                    className="mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-all duration-200"
                    style={{ color: '#A51C30' }}
                  >
                    Learn More
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Patient Portal Section ── */}
      <section id="patient-portal" className="py-20 lg:py-24" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.5px' }}>Patient Portal</h2>
            <p className="mt-3 max-w-2xl mx-auto text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
              Manage your health from anywhere. Book appointments, track your lung health, read expert articles, and connect with our care team — all in one place.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {/* Large card — Book Appointment */}
            <div
              className="lg:col-span-1 rounded-2xl p-7 flex flex-col justify-between text-white transition-all duration-300"
              style={{ background: 'linear-gradient(135deg, #8B1A1A 0%, #A51C30 60%, #C0392B 100%)', boxShadow: '0 20px 60px rgba(139,26,26,0.25)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(139,26,26,0.35)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(139,26,26,0.25)'; }}
            >
              <div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Book Appointment</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  Schedule your visit online — pick a date, time slot, and appointment type in minutes.
                </p>
              </div>
              <Link
                href="/patient/book-appointment"
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-all self-start"
                style={{ background: '#fff', color: '#A51C30' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
              >
                Book Now
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* My Health */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-between transition-all duration-300"
              style={{ background: '#FFF5F5', border: '1px solid rgba(139,26,26,0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.06)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.06)'; }}
            >
              <div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(165,28,48,0.1)' }}>
                  <svg className="w-5 h-5" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold mb-1.5" style={{ color: '#111827' }}>My Health</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>Track SpO₂ trends, medications, and visit history.</p>
              </div>
              <Link href="/patient/my-health" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-all" style={{ color: '#A51C30' }}>
                View Health <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>

            {/* Lung Academy */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-between transition-all duration-300"
              style={{ background: '#FFFBF0', border: '1px solid rgba(217,119,6,0.12)', boxShadow: '0 20px 60px rgba(0,0,0,0.06)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.06)'; }}
            >
              <div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(217,119,6,0.1)' }}>
                  <svg className="w-5 h-5" style={{ color: '#D97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-base font-bold mb-1.5" style={{ color: '#111827' }}>Lung Academy</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>Expert articles on COPD, Asthma, TB, and lung health.</p>
              </div>
              <Link href="/patient/lung-academy" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-all" style={{ color: '#D97706' }}>
                Start Reading <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>

            {/* Ask a Question */}
            <div
              className="rounded-2xl p-6 flex flex-col justify-between transition-all duration-300"
              style={{ background: '#F0F7FF', border: '1px solid rgba(2,132,199,0.12)', boxShadow: '0 20px 60px rgba(0,0,0,0.06)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.06)'; }}
            >
              <div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(2,132,199,0.1)' }}>
                  <svg className="w-5 h-5" style={{ color: '#0284C7' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-base font-bold mb-1.5" style={{ color: '#111827' }}>Ask a Question</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>Send a health query directly to the clinic care team.</p>
              </div>
              <Link href="/patient/ask-question" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-all" style={{ color: '#0284C7' }}>
                Ask Now <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>

            {/* Patient Home */}
            <div
              className="sm:col-span-2 lg:col-span-1 rounded-2xl p-6 flex flex-col justify-between transition-all duration-300"
              style={{ background: '#111827', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.22)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.15)'; }}
            >
              <div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-white mb-1.5">Patient Home</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>Your personal dashboard — appointments, stats, and daily health tips.</p>
              </div>
              <Link href="/patient" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-all" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Go to Dashboard <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </Link>
            </div>
          </div>

          {/* CTA Banner */}
          <div
            className="rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
            style={{ background: 'linear-gradient(135deg, #8B1A1A 0%, #A51C30 100%)', boxShadow: '0 20px 60px rgba(139,26,26,0.3)' }}
          >
            <div>
              <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Ready to take charge of your lung health?</h3>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Access all features of the patient portal — free for all clinic patients.</p>
            </div>
            <Link
              href="/patient-login"
              className="flex-shrink-0 inline-flex items-center gap-2 font-bold px-7 py-3 rounded-full transition-all"
              style={{ background: '#fff', color: '#A51C30', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Open Patient App
            </Link>
          </div>
        </div>
      </section>

      {/* ── Google Reviews Section ── */}
      <section className="py-20 lg:py-24" style={{ background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
            </div>
            <div className="flex items-center justify-center gap-3 mb-3">
              {/* Google Logo SVG */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-8 h-8">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              <h2 className="text-3xl lg:text-4xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.5px' }}>
                Patient Reviews
              </h2>
            </div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((s) => (
                  <svg key={s} className="w-6 h-6" viewBox="0 0 24 24" fill={s <= 4 ? '#FBBC05' : 'none'} stroke="#FBBC05" strokeWidth="1.5">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <span className="text-2xl font-bold" style={{ color: '#111827' }}>4.8</span>
              <span className="text-sm font-medium" style={{ color: '#6B7280' }}>Rated on Google</span>
            </div>
            <p className="text-sm" style={{ color: '#9CA3AF' }}>What our patients say about Dr. Neeraj Mumwalia</p>
          </div>

          {/* Review Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                name: 'Patient from RS Pura',
                initials: 'PR',
                review: 'Very good doctor in R S pura, I am suffering from chest problem good treatment given by dr neeraj, now I feel better.',
                rating: 5,
                location: 'RS Pura, Jammu',
              },
              {
                name: 'Verified Patient',
                initials: 'VP',
                review: 'Very Honest Doctor, Care for every single patient with heart, Highly recommended.',
                rating: 5,
                location: 'Jammu',
              },
              {
                name: 'Local Patient',
                initials: 'LP',
                review: 'The best doctor I ever seen, He treated patients with helpful diagnosis.',
                rating: 5,
                location: 'Jammu',
              },
              {
                name: 'Rahul from Jammu',
                initials: 'RJ',
                review: 'Dr. Neeraj is very knowledgeable and patient. He explained my lung condition clearly and the treatment worked very well. Highly recommended in Jammu.',
                rating: 5,
                location: 'Jammu City',
              },
              {
                name: 'Sunita Devi',
                initials: 'SD',
                review: 'Bahut achhe doctor hain. Meri asthma ki problem ka sahi ilaj kiya. Clinic bhi saaf aur staff bhi bahut helpful hai. Thank you Dr. Neeraj.',
                rating: 5,
                location: 'RS Pura, Jammu',
              },
              {
                name: 'Mohan Lal',
                initials: 'ML',
                review: 'Best pulmonologist in Jammu region. Came from Akhnoor for consultation and it was worth it. Very thorough examination and clear guidance on COPD management.',
                rating: 5,
                location: 'Akhnoor, Jammu',
              },
            ].map((review, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl flex flex-col gap-4 transition-all duration-300"
                style={{ background: '#FAFAFA', border: '1px solid #F0F0F0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 36px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)'; }}
              >
                {/* Stars */}
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map((s) => (
                    <svg key={s} className="w-4 h-4" viewBox="0 0 24 24" fill={s <= review.rating ? '#FBBC05' : '#E5E7EB'}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                {/* Review text */}
                <p className="text-sm leading-relaxed flex-1" style={{ color: '#374151' }}>
                  &ldquo;{review.review}&rdquo;
                </p>
                {/* Reviewer */}
                <div className="flex items-center gap-3 pt-2 border-t" style={{ borderColor: '#F0F0F0' }}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: '#A51C30' }}>
                    {review.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: '#111827' }}>{review.name}</p>
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>{review.location}</p>
                  </div>
                  {/* Google G icon */}
                  <div className="ml-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://www.google.com/search?q=dr+neeraj+mumwalia+centre#lrd=0x3920b0c0c0c0c0c:0x0,1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full transition-all duration-200 underline"
              style={{ background: '#4285F4', color: '#fff', boxShadow: '0 4px 16px rgba(66,133,244,0.35)', textDecorationColor: 'rgba(255,255,255,0.6)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(66,133,244,0.5)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(66,133,244,0.35)'; }}
            >
              ⭐ View All 33 Reviews on Google
            </a>
            <a
              href="https://maps.google.com/?q=Dr+Neeraj+Mumwalia+RS+Pura+Jammu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold px-7 py-3 rounded-full transition-all duration-200 underline"
              style={{ background: '#fff', color: '#4285F4', border: '2px solid #4285F4', textDecorationColor: 'rgba(66,133,244,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.background = '#F0F7FF'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.background = '#fff'; }}
            >
              📍 View on Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* ── Contact Section ── */}
      <section id="contact" className="py-20 lg:py-24" style={{ background: '#F8F5F0' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-3 h-3 rounded-full" style={{ background: '#A51C30' }} />
              <div className="w-8 h-1 rounded-full" style={{ background: '#A51C30' }} />
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.5px' }}>Contact Us</h2>
            <p className="mt-3 text-sm" style={{ color: '#9CA3AF' }}>Reach out to book an appointment or for any queries</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                title: 'Clinic Address',
                lines: ['Thikrian, Main Road R.S. Pura', 'Near Tata Motors Car Showroom'],
              },
              {
                icon: (
                  <svg className="w-6 h-6" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                ),
                title: 'Phone',
                lines: ['6005969059', '8082317910'],
              },
              {
                icon: (
                  <svg className="w-6 h-6" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                title: 'Email',
                lines: ['contact@drneerajlungcare.in', 'We reply within 24 hours'],
              },
            ].map((card) => (
              <div
                key={card.title}
                className="text-center p-6 rounded-2xl transition-all duration-300"
                style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.04)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 28px 70px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 60px rgba(0,0,0,0.06)'; }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#FFF5F5' }}>
                  {card.icon}
                </div>
                <h3 className="font-bold mb-2" style={{ color: '#111827' }}>{card.title}</h3>
                {card.title === 'Clinic Address' ? (
                  <a
                    href="https://maps.google.com/?q=Dr+Neeraj+Mumwalia+RS+Pura+Jammu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm block underline transition-colors"
                    style={{ color: '#4285F4' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#1A73E8')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#4285F4')}
                  >
                    {card.lines.join(', ')}
                  </a>
                ) : (
                  card.lines.map((line) => (
                    card.title === 'Email' && line.includes('@') ? (
                      <a key={line} href={`mailto:${line}`} className="text-sm block transition-colors" style={{ color: '#A51C30' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#8B1A1A')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#A51C30')}
                      >{line}</a>
                    ) : (
                      <p key={line} className="text-sm" style={{ color: '#9CA3AF' }}>{line}</p>
                    )
                  ))
                )}
              </div>
            ))}
          </div>

          {/* ── Clinic & Doctor Timing Cards ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
            {/* Card 1: Clinic Hours */}
            <div
              className="rounded-2xl p-7 flex flex-col gap-4"
              style={{ background: '#fff', border: '2px solid rgba(165,28,48,0.15)', boxShadow: '0 8px 32px rgba(165,28,48,0.08)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FFF5F5' }}>
                  <svg className="w-6 h-6" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#A51C30' }}>Clinic Hours</p>
                  <p className="text-sm font-semibold" style={{ color: '#6B7280' }}>Open All Days</p>
                </div>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#FFF5F5' }}>
                <p className="text-2xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif" }}>8:00 AM – 8:00 PM</p>
                <p className="text-base font-semibold mt-1" style={{ color: '#A51C30' }}>Monday to Sunday</p>
                <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#ECFDF5', color: '#059669' }}>
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#059669' }} />
                  Open 7 Days a Week
                </div>
              </div>
            </div>

            {/* Card 2: Doctor Consultation */}
            <div
              className="rounded-2xl p-7 flex flex-col gap-4"
              style={{ background: '#fff', border: '2px solid rgba(165,28,48,0.15)', boxShadow: '0 8px 32px rgba(165,28,48,0.08)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FFF5F5' }}>
                  <svg className="w-6 h-6" style={{ color: '#A51C30' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H7a2 2 0 00-2 2v4a6 6 0 0012 0V5a2 2 0 00-2-2h-2M9 3v2m6-2v2M9 3h6" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14v4m0 0h-2m2 0h2" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#A51C30' }}>Doctor Consultation</p>
                  <p className="text-sm font-semibold" style={{ color: '#6B7280' }}>Dr. Neeraj Mumwalia</p>
                </div>
              </div>
              <div className="rounded-xl p-4 space-y-3" style={{ background: '#FFF5F5' }}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#6B7280' }}>Mon – Sat</p>
                  <p className="text-xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif" }}>5:30 PM – 8:00 PM</p>
                </div>
                <div className="border-t pt-3" style={{ borderColor: 'rgba(165,28,48,0.1)' }}>
                  <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#6B7280' }}>Sunday</p>
                  <p className="text-xl font-extrabold" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif" }}>10:30 AM – 5:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Appointment Booking Form ── */}
          <div className="max-w-2xl mx-auto">
            <div
              className="rounded-2xl overflow-hidden"
              style={{ background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.04)' }}
            >
              <div
                className="px-8 py-6"
                style={{ background: 'linear-gradient(135deg, #8B1A1A 0%, #A51C30 100%)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>Book an Appointment</h3>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>Fill in your details and we will confirm your slot</p>
                  </div>
                </div>
              </div>

              <div className="px-8 py-7">
                {bookingSuccess ? (
                  <div className="text-center py-8">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ background: '#ECFDF5' }}
                    >
                      <svg className="w-8 h-8" style={{ color: '#059669' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold mb-2" style={{ color: '#111827', fontFamily: "'Poppins', sans-serif" }}>
                      Appointment request sent!
                    </h4>
                    <p className="text-sm mb-6" style={{ color: '#6B7280' }}>
                      Doctor will call you to confirm your appointment.
                    </p>
                    <button
                      onClick={() => setBookingSuccess(false)}
                      className="text-sm font-semibold px-6 py-2.5 rounded-full transition-all"
                      style={{ background: '#A51C30', color: '#fff', boxShadow: '0 4px 14px rgba(165,28,48,0.3)' }}
                    >
                      Book Another
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleBookingSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={bookingForm.name}
                          onChange={handleBookingChange}
                          placeholder="Your full name"
                          required
                          className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all"
                          style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: '#111827' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#A51C30'; e.currentTarget.style.background = '#fff'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#F9FAFB'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={bookingForm.phone}
                          onChange={handleBookingChange}
                          placeholder="10-digit mobile number"
                          required
                          className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all"
                          style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: '#111827' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#A51C30'; e.currentTarget.style.background = '#fff'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#F9FAFB'; }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
                          Preferred Date *
                        </label>
                        <input
                          type="date"
                          name="date"
                          value={bookingForm.date}
                          onChange={handleBookingChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all"
                          style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: '#111827' }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#A51C30'; e.currentTarget.style.background = '#fff'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#F9FAFB'; }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
                          Preferred Time *
                        </label>
                        <select
                          name="time"
                          value={bookingForm.time}
                          onChange={handleBookingChange}
                          required
                          disabled={!bookingForm.date}
                          className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all"
                          style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: bookingForm.time ? '#111827' : '#9CA3AF', cursor: !bookingForm.date ? 'not-allowed' : 'pointer', opacity: !bookingForm.date ? 0.6 : 1 }}
                          onFocus={(e) => { e.currentTarget.style.borderColor = '#A51C30'; e.currentTarget.style.background = '#fff'; }}
                          onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#F9FAFB'; }}
                        >
                          <option value="">{bookingForm.date ? 'Select a time slot' : 'Select date first'}</option>
                          {availableSlots.map((slot) => (
                            <option key={slot.value} value={slot.value}>{slot.label}</option>
                          ))}
                        </select>
                        {bookingForm.date && (
                          <p className="mt-1 text-xs" style={{ color: '#6B7280' }}>
                            {(() => {
                              const d = new Date(bookingForm.date + 'T00:00:00');
                              return d.getDay() === 0
                                ? 'Sunday: 10:30 AM – 5:00 PM (every 30 min)'
                                : 'Mon–Sat: 5:30 PM – 7:30 PM (evening slots)';
                            })()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#374151' }}>
                        Reason / Symptoms
                      </label>
                      <textarea
                        name="reason"
                        value={bookingForm.reason}
                        onChange={handleBookingChange}
                        placeholder="Briefly describe your symptoms or reason for visit (optional)"
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all resize-none"
                        style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: '#111827' }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = '#A51C30'; e.currentTarget.style.background = '#fff'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#F9FAFB'; }}
                      />
                    </div>

                    {bookingError && (
                      <div
                        className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
                        style={{ background: '#FEF2F2', color: '#DC2626' }}
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {bookingError}
                      </div>
                    )}

                    {/* Timing note */}
                    <div className="flex items-start gap-2 px-4 py-3 rounded-xl" style={{ background: '#FFFBF0', border: '1px solid rgba(217,119,6,0.2)' }}>
                      <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs leading-relaxed" style={{ color: '#92400E' }}>
                        <span className="font-bold">Clinic open 8AM–8PM daily (Mon–Sun).</span> Doctor consultation: Mon–Sat 5:30–8:00 PM · Sunday 10:30 AM–5:00 PM
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={bookingLoading}
                      className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-200 flex items-center justify-center gap-2"
                      style={{
                        background: bookingLoading ? '#D1D5DB' : '#A51C30',
                        cursor: bookingLoading ? 'not-allowed' : 'pointer',
                        boxShadow: bookingLoading ? 'none' : '0 4px 16px rgba(165,28,48,0.3)',
                      }}
                    >
                      {bookingLoading ? (
                        <>
                          <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                          Sending Request…
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Request Appointment
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8" style={{ background: '#111827' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#A51C30' }}>
              <svg viewBox="0 0 40 40" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="14" cy="20" rx="7" ry="11" opacity="0.9" />
                <ellipse cx="26" cy="20" rx="7" ry="11" opacity="0.9" />
                <rect x="18" y="9" width="4" height="22" rx="2" fill="white" opacity="0.6" />
              </svg>
            </div>
            <span className="text-white font-semibold text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>Centre for Lung Care and Chest Diseases</span>
          </div>
          <p className="text-xs text-center" style={{ color: '#6B7280' }}>© 2026 drneerajlungcare.in · Clinic: 8AM–8PM (Mon–Sun) | Doctor: 5:30–8PM Mon–Sat, 10:30AM–5PM Sun</p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            {NAV_LINKS.map((link) => (
              <a key={link.label} href={link.href} className="text-xs transition-colors" style={{ color: '#6B7280' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
              >
                {link.label}
              </a>
            ))}
            <Link href="/patient-login" className="text-xs font-semibold transition-colors" style={{ color: '#A51C30' }}>
              Patient App
            </Link>
          </div>
        </div>
      </footer>

      {/* ── Floating Contact Button ── */}
      <a
        href="#contact"
        className="fixed bottom-6 right-6 z-50 text-white text-sm font-semibold px-5 py-3 rounded-full flex items-center gap-2 transition-all duration-200"
        style={{ background: '#A51C30', boxShadow: '0 8px 32px rgba(165,28,48,0.45)' }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 40px rgba(165,28,48,0.55)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(165,28,48,0.45)'; }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        Contact us
      </a>
    </div>
  );
}